import React from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MapPin, Phone, Instagram, Linkedin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ASSETS } from "@/data/content";

const Footer = () => {
  const { t } = useLang();
  const navigate = useNavigate();

  const goSection = (id) => {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    }, 300);
  };

  return (
    <footer data-testid="main-footer" className="bg-[#141414] text-[#E8E2D6]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-12">
        <div className="border-b border-white/10 pb-16 mb-16">
          <p className="text-xs uppercase tracking-[0.28em] text-[#C4A27A] mb-4">
            {t.footer.tagline}
          </p>
          <button
            data-testid="footer-cta"
            onClick={() => navigate("/contact")}
            className="font-display text-4xl md:text-6xl lg:text-7xl leading-tight text-start hover:text-[#C4A27A] transition-colors"
          >
            {t.footer.cta} →
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={ASSETS.companyLogo}
                alt="logo"
                className="h-14 w-14 object-contain rounded-full bg-white p-1.5"
              />
              <span className="font-display text-lg max-w-[220px]">{t.company.name}</span>
            </div>
            <p className="text-sm text-[#A8A296] max-w-sm leading-relaxed">
              {t.hero.overview}
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C4A27A] mb-5">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-3 text-sm">
              {["about", "brands", "b2b", "quality", "clients"].map((k) => (
                <li key={k}>
                  <button
                    onClick={() => goSection(k === "about" ? "about" : k)}
                    className="text-[#D8D2C6] hover:text-white transition-colors"
                  >
                    {t.nav[k]}
                  </button>
                </li>
              ))}
              <li>
                <button onClick={() => navigate("/careers")} className="text-[#D8D2C6] hover:text-white transition-colors">
                  {t.nav.careers}
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#C4A27A] mb-5">
              {t.footer.contact}
            </h4>
            <ul className="space-y-4 text-sm text-[#D8D2C6]">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#C4A27A] shrink-0" />
                <span>{t.contact.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#C4A27A] shrink-0" />
                <a href="mailto:info@ezdhar-ksa.com" className="hover:text-white transition-colors">
                  info@ezdhar-ksa.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#C4A27A] shrink-0" />
                <a href="mailto:jobs.hr@ezdhar-ksa.com" className="hover:text-white transition-colors">
                  jobs.hr@ezdhar-ksa.com
                </a>
              </li>
            </ul>
            <div className="flex items-center gap-4 mt-6">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/alezdhar.sa/", label: "Instagram" },
                { Icon: Linkedin, href: "https://www.linkedin.com/company/alezdhar-alrafah-trading-ltd", label: "LinkedIn" },
              ].map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-10 h-10 grid place-items-center border border-white/15 rounded-full hover:bg-[#6A1E27] hover:border-[#6A1E27] transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-[#8A8478] flex flex-col md:flex-row items-center justify-between gap-3">
          <span>
            © {new Date().getFullYear()} {t.company.name}. {t.footer.rights}.
          </span>
          <span>Jeddah, Saudi Arabia</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
